import { formatNumber } from "@/lib/utils/format";
import { ActionCenter } from "../../ActionCenter";
import { UpcomingEvents } from "../../UpcomingEvents";
import {
  useAssetStats,
  useComplianceStats,
  useDocumentStats,
  useExitStats,
  usePerformanceStats,
  useProbationStats,
  useRecruitmentStats,
} from "./data";
import { KpiWidget, SummaryWidget, ViewAllLink, WidgetCard, type SummaryRow, type WidgetProps } from "./kit";
import type { DataState } from "./state";

function stateOf(q: { isLoading: boolean; isError: boolean; fetchStatus: string; refetch: () => unknown }): DataState {
  return { isLoading: q.isLoading && q.fetchStatus !== "idle", isError: q.isError, refetch: q.refetch };
}

const warnIf = (n: number): SummaryRow["tone"] => (n > 0 ? "warn" : "default");

// ------------------------------------------------------- tasks & events

/** Role-aware "what needs me right now" list; it gates and fetches its own data. */
export function ActionCenterWidget(_: WidgetProps) {
  return <ActionCenter className="h-full min-w-0" />;
}

export function UpcomingEventsWidget(_: WidgetProps) {
  return <UpcomingEvents className="h-full min-w-0" />;
}

// ------------------------------------------------------- resignation & exit

export function ExitSummaryWidget(_: WidgetProps) {
  const q = useExitStats();
  const s = q.data;
  return (
    <SummaryWidget
      title="Resignation & Exit"
      state={stateOf(q)}
      link={{ to: "/exit-dashboard" }}
      rows={
        s && [
          { label: "Serving notice period", value: formatNumber(s.employees_in_notice) },
          { label: "Exits this month", value: formatNumber(s.exits_this_month) },
          { label: "Exit clearance pending", value: formatNumber(s.pending_clearance), tone: warnIf(s.pending_clearance) },
          { label: "Clearance overdue", value: formatNumber(s.overdue_clearance), tone: warnIf(s.overdue_clearance) },
          { label: "Settlements pending", value: formatNumber(s.settlement_pending), tone: warnIf(s.settlement_pending) },
        ]
      }
    />
  );
}

export function NoticePeriodWidget(_: WidgetProps) {
  const q = useExitStats();
  return (
    <KpiWidget
      label="Serving notice period"
      icon="logout"
      tone="amber"
      to="/exit-dashboard"
      state={stateOf(q)}
      value={q.data ? formatNumber(q.data.employees_in_notice) : undefined}
    />
  );
}

export function PendingClearanceWidget(_: WidgetProps) {
  const q = useExitStats();
  return (
    <KpiWidget
      label="Exit clearance pending"
      icon="clipboard-list"
      tone="accent"
      to="/exit-dashboard"
      state={stateOf(q)}
      value={q.data ? formatNumber(q.data.pending_clearance) : undefined}
      hint={q.data && q.data.overdue_clearance > 0 ? `${q.data.overdue_clearance} overdue` : undefined}
    />
  );
}

// ------------------------------------------------------------- probation

export function ProbationSummaryWidget(_: WidgetProps) {
  const q = useProbationStats();
  const s = q.data;
  return (
    <SummaryWidget
      title="Probation & Confirmation"
      state={stateOf(q)}
      link={{ to: "/onboarding-probation" }}
      rows={
        s && [
          { label: "Currently on probation", value: formatNumber(s.on_probation) },
          { label: "Ending in the next 30 days", value: formatNumber(s.ending_in_30_days), tone: warnIf(s.ending_in_30_days) },
          { label: "Reviews overdue", value: formatNumber(s.review_overdue), tone: warnIf(s.review_overdue) },
          { label: "Extended", value: formatNumber(s.extended) },
        ]
      }
    />
  );
}

export function OnProbationWidget(_: WidgetProps) {
  const q = useProbationStats();
  return (
    <KpiWidget
      label="On probation"
      icon="users"
      tone="indigo"
      to="/onboarding-probation"
      state={stateOf(q)}
      value={q.data ? formatNumber(q.data.on_probation) : undefined}
    />
  );
}

export function ProbationEndingWidget(_: WidgetProps) {
  const q = useProbationStats();
  return (
    <KpiWidget
      label="Probation ending soon"
      icon="calendar"
      tone="amber"
      to="/onboarding-probation"
      state={stateOf(q)}
      value={q.data ? formatNumber(q.data.ending_in_30_days) : undefined}
      hint="next 30 days"
    />
  );
}

// ------------------------------------------------------------ recruitment

export function RecruitmentSummaryWidget(_: WidgetProps) {
  const q = useRecruitmentStats();
  const s = q.data;
  return (
    <SummaryWidget
      title="Recruitment"
      state={stateOf(q)}
      link={{ to: "/recruitment/dashboard" }}
      rows={
        s && [
          { label: "Open positions", value: formatNumber(s.open_requisitions) },
          { label: "Candidates in pipeline", value: formatNumber(s.active_candidates) },
          { label: "Interviews today", value: formatNumber(s.interviews_today) },
          { label: "Offers awaiting acceptance", value: formatNumber(s.offers_pending), tone: warnIf(s.offers_pending) },
          { label: "Offers accepted", value: formatNumber(s.offers_accepted), tone: "good" },
          { label: "Joined this month", value: formatNumber(s.hires_this_month), tone: "good" },
        ]
      }
    />
  );
}

export function OpenPositionsWidget(_: WidgetProps) {
  const q = useRecruitmentStats();
  return (
    <KpiWidget
      label="Open positions"
      icon="briefcase"
      tone="primary"
      to="/recruitment/dashboard"
      state={stateOf(q)}
      value={q.data ? formatNumber(q.data.open_requisitions) : undefined}
      hint={q.data ? `${q.data.active_candidates} candidates` : undefined}
    />
  );
}

const FUNNEL_ORDER = ["applied", "screening", "shortlisted", "interview", "assessment", "final_review", "offer", "hired"] as const;

export function RecruitmentFunnelWidget(_: WidgetProps) {
  const q = useRecruitmentStats();
  const funnel = q.data?.hiring_funnel ?? {};
  const rows = FUNNEL_ORDER.map((stage) => ({ stage, count: funnel[stage] ?? 0 }));
  const max = Math.max(1, ...rows.map((r) => r.count));
  const empty = rows.every((r) => r.count === 0);
  return (
    <WidgetCard title="Recruitment Funnel" state={stateOf(q)} action={<ViewAllLink to="/recruitment/dashboard" />}>
      {empty ? (
        <p className="py-6 text-center text-sm text-slate-400">No candidates in the pipeline yet.</p>
      ) : (
        <div className="space-y-3">
          {rows.map(({ stage, count }) => (
            <div key={stage}>
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="font-medium capitalize text-slate-700">{stage.replace(/_/g, " ")}</span>
                <span className="tabular-nums text-slate-500">{count}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-primary-600" style={{ width: `${(count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}

// ------------------------------------------------------------ performance

export function PerformanceSummaryWidget(_: WidgetProps) {
  const q = usePerformanceStats();
  const s = q.data;
  const hasCycle = Boolean(s?.active_cycle);
  return (
    <SummaryWidget
      title="Performance Reviews"
      state={stateOf(q)}
      link={{ to: "/performance/dashboard" }}
      note={s?.active_cycle ? `Cycle: ${s.active_cycle.name}` : undefined}
      rows={
        s && hasCycle
          ? [
              { label: "Self reviews pending", value: formatNumber(s.pending_self_reviews ?? 0), tone: warnIf(s.pending_self_reviews ?? 0) },
              { label: "Manager reviews pending", value: formatNumber(s.pending_manager_reviews ?? 0), tone: warnIf(s.pending_manager_reviews ?? 0) },
              { label: "Reviews completed", value: formatNumber(s.completed_reviews ?? 0), tone: "good" },
              { label: "Goal completion", value: `${formatNumber(s.goal_completion_percent ?? 0)}%` },
              { label: "Improvement plans", value: formatNumber(s.pip_count ?? 0) },
            ]
          : undefined
      }
    />
  );
}

// ----------------------------------------------- documents & compliance

export function DocumentsSummaryWidget(_: WidgetProps) {
  const q = useDocumentStats();
  const s = q.data;
  return (
    <SummaryWidget
      title="Employee Documents"
      state={stateOf(q)}
      link={{ to: "/documents/dashboard" }}
      rows={
        s && [
          { label: "Pending verification", value: formatNumber(s.pending_verification), tone: warnIf(s.pending_verification) },
          { label: "Mandatory documents missing", value: formatNumber(s.missing_documents), tone: warnIf(s.missing_documents) },
          { label: "Expiring soon", value: formatNumber(s.expiring_documents), tone: warnIf(s.expiring_documents) },
          { label: "Rejected", value: formatNumber(s.rejected_documents) },
          { label: "Compliance", value: `${formatNumber(s.compliance_percentage, 0)}%` },
        ]
      }
    />
  );
}

export function PendingVerificationWidget(_: WidgetProps) {
  const q = useDocumentStats();
  return (
    <KpiWidget
      label="Documents to verify"
      icon="file-text"
      tone="amber"
      to="/documents/dashboard"
      state={stateOf(q)}
      value={q.data ? formatNumber(q.data.pending_verification) : undefined}
    />
  );
}

export function ComplianceSummaryWidget(_: WidgetProps) {
  const q = useComplianceStats();
  const s = q.data;
  return (
    <SummaryWidget
      title="Statutory Compliance"
      state={stateOf(q)}
      link={{ to: "/compliance/dashboard" }}
      rows={
        s && [
          { label: "Total filings", value: formatNumber(s.total_filings) },
          { label: "Pending filings", value: formatNumber(s.pending_filings), tone: warnIf(s.pending_filings) },
          { label: "Overdue filings", value: formatNumber(s.overdue_filings), tone: warnIf(s.overdue_filings) },
          { label: "Tax declarations to verify", value: formatNumber(s.pending_declaration_verification), tone: warnIf(s.pending_declaration_verification) },
        ]
      }
    />
  );
}

// ------------------------------------------------------------------ assets

export function AssetsSummaryWidget(_: WidgetProps) {
  const q = useAssetStats();
  const s = q.data;
  return (
    <SummaryWidget
      title="Assets"
      state={stateOf(q)}
      link={{ to: "/assets/dashboard" }}
      rows={
        s && [
          { label: "Assets in total", value: formatNumber(s.total) },
          { label: "Currently assigned", value: formatNumber(s.assigned) },
          { label: "Return pending", value: formatNumber(s.awaiting_return), tone: warnIf(s.awaiting_return) },
          { label: "In maintenance", value: formatNumber(s.in_maintenance) },
          { label: "Warranty expiring", value: formatNumber(s.warranty_expiring), tone: warnIf(s.warranty_expiring) },
        ]
      }
    />
  );
}
